import React, { useState, useEffect } from "react";
import "./SubAdminDashboard.css";
import { BASE_URL } from "./Config";
import { useNavigate } from "react-router-dom";
import { FiTrash2 } from 'react-icons/fi';
import { FiMenu } from 'react-icons/fi';
import DatePicker from "./DatePicker";
import { isSuperAdmin } from "../utils/auth";
import AlertCard from "./AlertCard";
import logoImage from "../resources/1000028180.jpeg";

export default function SubAdminDashboard() {
  const [subAdmins, setSubAdmins] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [subadminToDelete, setSubadminToDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [formData, setFormData] = useState({
    user_name: "",
    password: "",
    full_name: "",
    date_of_birth: null,
    gender: "",
    organization: "",
    sport: "",
  });
  const [orgOptions, setOrgOptions] = useState([]);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuBtnStyle = {
    background: 'none',
    border: 'none',
    textAlign: 'left',
    padding: '12px 20px',
    fontSize: '1rem',
    cursor: 'pointer',
    width: '100%',
    transition: 'background 0.18s',
    borderBottom: '1px solid #f1f1f1',
    color: 'gray',
  };
  const [alertCard, setAlertCard] = useState({ show: false, message: '', type: 'error', title: '' });
  function logout() {
    navigate('/logout');
  }

  useEffect(() => {
    fetchAllSubAdmins();
    fetchActivityLog();
    fetchOrganizations();
  }, []);

  // Fetch all sub-admins from backend
  function fetchAllSubAdmins() {
    const token = localStorage.getItem("admin_token");
    fetch(`${BASE_URL}/api/admin/subadmin_data`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setSubAdmins(data);
      })
      .catch((err) => {
        setSubAdmins([]);
      });
  }

  // Fetch activity log
  function fetchActivityLog() {
    const token = localStorage.getItem("admin_token");
    fetch(`${BASE_URL}/api/admin/audit_log`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setActivityLog(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        setActivityLog([]);
      });
  }

  function fetchOrganizations() {
    const token = localStorage.getItem("admin_token");
    fetch(`${BASE_URL}/api/organization/get_organizations`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setOrgOptions(Array.isArray(data) ? data : []);
      })
      .catch(() => setOrgOptions([]));
  }

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleDateChange = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      date_of_birth: e.target.value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCreateLoading(true);
    // Find the selected organization object
    const selectedOrg = orgOptions.find(org => org.name === formData.organization);
    const payload = {
      user_name: formData.user_name,
      password: formData.password,
      full_name: formData.full_name,
      date_of_birth: formData.date_of_birth
        ? (formData.date_of_birth instanceof Date
            ? formData.date_of_birth.toISOString().split('T')[0]
            : formData.date_of_birth)
        : "",
      gender: formData.gender,
      sport: formData.sport || "none",
      org_id: selectedOrg ? selectedOrg.org_id : "",
      role_id: "2"
    };
    const token = localStorage.getItem("admin_token");
    fetch(`${BASE_URL}/api/admin/create_subadmin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        return res.json().then(data => ({ data, status: res.status }));
      })
      .then(({ data, status }) => {
        // Check for success in different possible response formats
        if (data.success || data.status === 201 || status === 201 || status === 200) {
          // Close modal and reset form immediately on success
          setShowCreateModal(false);
          setFormData({
            user_name: "",
            password: "",
            full_name: "",
            date_of_birth: null,
            gender: "",
            organization: "",
            sport: "none",
          });
          fetchAllSubAdmins(); // Refresh the list
        } else {
          setAlertCard({ show: true, message: "Error creating sub-admin: " + (data.message || "Unknown error"), type: 'error', title: 'Error' });
        }
        setCreateLoading(false);
      })
      .catch((err) => {
        setAlertCard({ show: true, message: "Error creating sub-admin: " + err.message, type: 'error', title: 'Error' });
        setCreateLoading(false);
      });
  };

  const handleCancel = () => {
    setShowCreateModal(false);
    setFormData({
      user_name: "",
      password: "",
      full_name: "",
      date_of_birth: null,
      gender: "",
      organization: "",
      sport: "none",
    });
  };

  // Handle delete subadmin
  const handleDeleteClick = (subadmin) => {
    setSubadminToDelete(subadmin);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!subadminToDelete) return;
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BASE_URL}/api/admin/delete_subadmin/${subadminToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to delete subadmin');
      setShowDeleteModal(false);
      setSubadminToDelete(null);
      fetchAllSubAdmins(); // Refresh the list
    } catch (err) {
      setAlertCard({ show: true, message: 'Error deleting subadmin: ' + err.message, type: 'error', title: 'Error' });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="subadmin-dashboard-bg">

<div className="org-header" style={{position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000}}>
        <div className="header-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div className="header-logo-container">
                                  <img
                        src={logoImage}
                        alt="Power Mind Metrics Logo"
                        style={{ height: "90px", width: "100px", marginRight: "10px" }}
                      />
            <h1 className="header-logo">Power Mind Metrics</h1>
          </div>
          <nav className="header-nav" style={{ marginLeft: "auto" }}>
            <button
              onClick={() => setMenuOpen((prev) => !prev)}
              className="menu-button"
              aria-label="Open menu"
            >
              <FiMenu size={24} />
            </button>
          </nav>
        </div>
        {menuOpen && (
          <div className="menu-dropdown">
            {isSuperAdmin() ? (
              <>
                <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); navigate('/AdminDashboard'); }}>Admin Dashboard</button>
                <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); navigate('/admin/sub-admins'); }}>Manage Subadmin</button>
                <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); navigate('/manage-users'); }}>Manage Users</button>
                <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); navigate('/organizations'); }}>Manage Organization</button>
              </>
            ) : null}
            {!isSuperAdmin() && (
              <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); navigate('/admin/sub-admins'); }}>Subadmin Dashboard</button>
            )}
            <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); navigate('/graph-report'); }}>Graph Report</button>

            <button style={menuBtnStyle} onClick={() => { setMenuOpen(false); logout(); }}>Logout</button>
          </div>
        )}
      </div>

      <div style={{marginTop: "84px"}}></div>
      {/* Sub-Admin Management Card */}
      <div className="subadmin-card">
        <div className="subadmin-card-header-row">
          <div className="subadmin-card-title" style={{ display: 'flex', flexDirection: 'column', marginRight: '0.5rem' }}>
            <span style={{ whiteSpace: 'nowrap' }}>Sub-Admin</span>
            <span style={{ fontWeight: 500 }}>Management</span>
          </div>
          <button className="create-subadmin-btn" onClick={() => setShowCreateModal(true)}>Create Sub-Admin</button>
        </div>
        <div className="subadmin-table-wrapper">
          <table className="subadmin-table">
            <thead>
              <tr>
                <th>Index <span className="sort-icon"></span></th>
                <th>Full Name <span className="sort-icon"></span></th>
                <th>Username <span className="sort-icon"></span></th>
                <th>Date of Birth <span className="sort-icon"></span></th>
                <th>Organization <span className="sort-icon"></span></th>
                <th>Gender <span className="sort-icon"></span></th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subAdmins.length > 0 ? (
                subAdmins.map((subadmin, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{subadmin.full_name}</td>
                    <td>{subadmin.user_name}</td>
                    <td>{subadmin.date_of_birth}</td>
                    <td>{subadmin.organization}</td>
                    <td>{subadmin.gender}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button 
                        className="icon-btn" 
                        title="Delete" 
                        onClick={() => handleDeleteClick(subadmin)}
                      >
                        <FiTrash2 size={20} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', color: '#888' }}>No sub-admins found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Activity Log Card */}
      <div className="subadmin-card">
        <div className="subadmin-card-title" style={{ marginBottom: '1.5rem' }}>Activity Log</div>
        <div className="subadmin-table-wrapper">
          <table className="subadmin-table">
            <thead>
              <tr>
                <th>Date <span className="sort-icon"></span></th>
                <th>User <span className="sort-icon"></span></th>
                <th>Action <span className="sort-icon"></span></th>
                <th>Details <span className="sort-icon"></span></th>
              </tr>
            </thead>
            <tbody>
              {activityLog.length > 0 ? (
                activityLog.map((log, i) => (
                  <tr key={i}>
                    <td>{log.timestamp}</td>
                    <td>{log.user}</td>
                    <td>{log.action}</td>
                    <td>{log.details}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center', color: '#888' }}>No activity log found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="subadmin-modal-overlay">
          <div className="subadmin-modal" style={{ maxWidth: 400, height: 'auto', gap: 24 }}>
            <h3 className="subadmin-modal-title" style={{ color: '#b91c1c' }}>Delete Sub-Admin</h3>
            <div>
              Are you sure you want to <b>delete {subadminToDelete?.full_name}?</b>
            </div>
            <div className="subadmin-modal-actions" style={{display: 'flex', justifyContent: 'space-between', gap: '10px'}}>
              <button
                type="button"
                className="subadmin-modal-cancel"
                onClick={() => { setShowDeleteModal(false); setSubadminToDelete(null); }}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="subadmin-modal-submit"
                onClick={confirmDelete}
                disabled={actionLoading}
              >
                {actionLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="subadmin-modal-overlay">
          <div className="subadmin-modal">
            <h3 className="subadmin-modal-title">Create Sub-Admin</h3>
            <form className="subadmin-modal-form" onSubmit={handleSubmit}>
              <div className="subadmin-modal-field">
                <label>Username</label>
                <input type="text" name="user_name" placeholder="Enter username" value={formData.user_name} onChange={handleFormChange} required />
              </div>
              <div className="subadmin-modal-field">
                <label>Password</label>
                <input type="password" name="password" placeholder="Enter password" value={formData.password} onChange={handleFormChange} required />
              </div>
              <div className="subadmin-modal-field">
                <label>Full Name</label>
                <input type="text" name="full_name" placeholder="Enter full name" value={formData.full_name} onChange={handleFormChange} required />
              </div>
              <div className="subadmin-modal-field">
                <label>Date of Birth</label>
                <DatePicker
                  value={formData.date_of_birth}
                  onChange={handleDateChange}
                  placeholder="Select date of birth"
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: 16, width: '100%',  marginTop: '10px'}}>
                <div className="subadmin-modal-field" style={{ flex: 1, minWidth: 0 }}>
                  <label>Gender</label>
                  <select name="gender" value={formData.gender} onChange={handleFormChange} required style={{ width: '100%', minWidth: 0, maxWidth: 260 }}>
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="subadmin-modal-field" style={{ flex: 1, minWidth: 0 }}>
                  <label>Organization</label>
                  <select name="organization" value={formData.organization} onChange={handleFormChange} required style={{ width: '100%', minWidth: 0, maxWidth: 260 }}>
                    <option value="">Select organization</option>
                    {orgOptions.map((org) => (
                      <option key={org.org_id} value={org.name}>{org.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="subadmin-modal-field">
                <label>Sport</label>
                <input
                  type="text"
                  name="sport"
                  placeholder="Enter sport"
                  value={formData.sport}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="subadmin-modal-actions" style={{display: 'flex', justifyContent: 'space-between', gap: '10px'}}>
                <button type="button" className="subadmin-modal-cancel" onClick={handleCancel}>Cancel</button>
                <button type="submit" className="subadmin-modal-submit" disabled={createLoading}>
                  {createLoading ? 'Creating...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
        padding: '12px 0',
        textAlign: 'center',
        fontFamily: 'Poppins, sans-serif',
        zIndex: 100
      }}>
        <p style={{
          margin: 0,
          fontSize: '14px',
          color: 'rgba(75, 75, 75, 0.8)',
          fontWeight: 400
        }}>
          © 2025 All rights reserved Power Mind Metrics
        </p>
      </div>
      
      <AlertCard show={alertCard.show} message={alertCard.message} type={alertCard.type} title={alertCard.title} onClose={() => setAlertCard({ ...alertCard, show: false })} />
    </div>
  );
}
